import { Component, Inject, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogModule as MatDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PlaidInstitutionSearchResult, PlaidService } from '../../../../../services/plaid.service';
import { SharedModule } from '../../../../shared/shared.module';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { ControlMessagesComponent } from '../../../../shared/control-messages/control-messages.component';

export interface ManualEntryDialogResult
{
    success: boolean;
    accountNumber?: string;
    routingNumber?: string;
    institutionMatch?: PlaidInstitutionSearchResult;
}

export interface ManualEntryDialogInput
{
    dialogTitle: string;
    dialogContent?: string;
    dialogError?: string;
    accountNumber?: string;
    routingNumber?: string;
    verifyPlaid?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'lf-manual-account-entry',
    templateUrl: './manual-account-entry.component.html',
    styleUrls: ['./manual-account-entry.component.scss'],
    standalone: true,
    imports: [
        // Shared
        SharedModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        // SD-9078: the template has always used <lf-control-messages>, but nothing imported
        // this standalone dialog, so its template was never compiled and the missing import
        // never surfaced. tsc does not typecheck Angular templates — only `ng build` does.
        ControlMessagesComponent,
        // Dependencies
    ],
})
export class ManualAccountEntryComponent implements OnInit
{
    form: UntypedFormGroup;
    hasSearchedForRoutingNumber = false;
    plaidSearchResults: PlaidInstitutionSearchResult[];
    selectedSearchResult: PlaidInstitutionSearchResult = null;

    constructor(
            private _dialogRef: MatDialogRef<ManualAccountEntryComponent>,
            private _builder: UntypedFormBuilder,
            private _plaidService: PlaidService,
            @Inject(MAT_DIALOG_DATA) public data: ManualEntryDialogInput,
    ) {
    }

    ngOnInit() {
        this.form = this._builder.group({
            routingNumber: [
                this.data.routingNumber,
                [
                    Validators.required,
                    Validators.minLength(9),
                    Validators.maxLength(9),
                ],
            ],
            accountNumber: [
                this.data.accountNumber,
                [
                    Validators.required,
                    Validators.minLength(6),
                ],
            ],
        });
    }

    searchForRoutingNumber() {
        if (this.data.verifyPlaid && this.form.get('routingNumber').value.toString().length === 9) {
            this._plaidService.searchInstitutions(this.form.get('routingNumber').value, 'transactions', true)
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (results: PlaidInstitutionSearchResult[]) => {
                            this.hasSearchedForRoutingNumber = true;
                            if (results.length > 0) {
                                this.plaidSearchResults = results;
                                this.selectedSearchResult = this.plaidSearchResults[0];
                            } else {
                                this.selectedSearchResult = null;
                                this.plaidSearchResults = [];
                            }
                        },
                    });
        }
    }


    closeDialog() {
        this._dialogRef.close(<ManualEntryDialogResult>{
            success: false,
        });
    }

    handleBankInfo() {
        this._dialogRef.close(<ManualEntryDialogResult>{
            success: true,
            ...this.form.getRawValue(),
            institutionMatch: this.selectedSearchResult,
        });
    }
}
